import { useEffect, useRef, useState } from 'react';

const fileDrag = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files');

// Accepts an .idml dropped ANYWHERE on the page (not just the dropzone), so an analysis is one
// drag away even from another tab. Listens only to file drags — in-page drags (sortable rows)
// use mouse events and never reach these handlers. Any file drop is swallowed (the browser's
// default would navigate away and lose on-screen results); only .idml files are passed on.
// Returns whether a file drag is currently over the page, for a drop-target overlay. A page that
// only sometimes offers the tools passes `enabled` so no listeners are attached otherwise.
const usePageIdmlDrop = (onFile: (file: File) => void, enabled = true) => {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const handler = useRef(onFile);
  handler.current = onFile;

  useEffect(() => {
    if (!enabled) return undefined;

    const enter = (event: DragEvent) => {
      if (!fileDrag(event)) return;
      depth.current += 1;
      setDragging(true);
    };
    const leave = (event: DragEvent) => {
      if (!fileDrag(event)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    };
    const over = (event: DragEvent) => {
      if (fileDrag(event)) event.preventDefault();
    };
    const drop = (event: DragEvent) => {
      if (!fileDrag(event)) return;
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      const dropped = event.dataTransfer?.files?.[0];
      if (dropped && dropped.name.toLowerCase().endsWith('.idml')) handler.current(dropped);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, [enabled]);

  return dragging;
};

export default usePageIdmlDrop;
